// Compact stacked card used on mobile viewports (<640px) for both current-month
// bills and Prior Month (carryover) bills.
import {
  Copy, Trash2, Zap, ExternalLink, CalendarClock, StickyNote,
} from "lucide-react";
import { displayFor } from "@/lib/bills-util";
import { EditableAmount, StatusDropdown } from "./table-cells";
import { shortDate, payPeriodClassFor } from "./utils";

export function MobileBillCard({ bill, today, ranges, isCarryover, onEdit, onReschedule, onDelete, onDup, onStatus, onAmount }) {
  const d = today ? displayFor(bill, today) : { col: "unpaid", notCleared: false };
  const periodClass = !isCarryover && ranges ? payPeriodClassFor(bill, ranges) : "";
  const containerBg = isCarryover
    ? "bg-white/60"
    : d.col === "unpaid"
    ? "bg-[#F9F8F6]"
    : d.notCleared
    ? "bg-[#FBF3E0]"
    : "bg-[#EEF3EC]";

  const amountAccent = isCarryover
    ? "neutral"
    : d.col === "unpaid" ? "unpaid" : d.notCleared ? "not-cleared" : "paid";
  const amountLabel = isCarryover
    ? "Amount"
    : d.col === "unpaid" ? "Unpaid" : d.notCleared ? "Paid — not cleared" : "Paid";

  return (
    <div className={`p-4 rounded-xl border-2 border-[#D8CFB9] ${containerBg}`} data-testid={`mobile-bill-${bill.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            {bill.is_autopay && (
              <span
                className="inline-block w-2 h-2 rounded-full bg-[#3B5B3E] shrink-0"
                aria-hidden="true"
                title="Autopay — no action needed"
                data-testid={`mobile-bill-autopay-dot-${bill.id}`}
              />
            )}
            <div className="text-[#2C302E] font-medium leading-snug">{bill.name}</div>
            {bill.notes && (
              <span
                className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-[#FEF3C7] text-[#7A5A1F] shrink-0"
                title={bill.notes}
                data-testid={`mobile-note-badge-${bill.id}`}
              >
                <StickyNote size={11} />
              </span>
            )}
          </div>
          <div className="text-xs text-[#8A918D] mt-0.5">
            Due{" "}
            <button
              type="button"
              className="text-left hover:text-[#4A6148] hover:underline decoration-dotted underline-offset-2 focus:outline-none"
              onClick={() => onReschedule(bill)}
              data-testid={`mobile-bill-due-date-${bill.id}`}
            >
              {shortDate(bill.due_date)}
            </button>
            {!isCarryover && ` · ${bill.is_recurring ? bill.recurrence_frequency : "one-time"}`}
          </div>
          {(bill.is_autopay || isCarryover || bill.pay_url || bill.original_due_date) && (
            <div className="mt-1.5 flex flex-wrap gap-1">
              {bill.pay_url && (
                <a
                  href={bill.pay_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#EEF3EC] text-[#3B5B3E] hover:bg-[#D5E1D3]"
                  data-testid={`mobile-bill-pay-online-${bill.id}`}
                  onClick={(e) => e.stopPropagation()}
                >
                  <ExternalLink size={9} /> pay online
                </a>
              )}
              {bill.is_autopay && (
                <span className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#EEF3EC] text-[#3B5B3E]">
                  <Zap size={9} /> autopay
                </span>
              )}
              {isCarryover && (
                <span className="text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#FDF2F0] text-[#8C3A30]">prior month</span>
              )}
              {bill.original_due_date && (
                <span
                  className="inline-flex items-center gap-0.5 text-[10px] font-semibold uppercase tracking-widest px-1.5 py-0.5 rounded-full bg-[#EDE7F6] text-[#4A3B7A]"
                  title={`Originally due ${bill.original_due_date}`}
                  data-testid={`mobile-rescheduled-pill-${bill.id}`}
                >
                  <CalendarClock size={9} /> rescheduled
                </span>
              )}
            </div>
          )}
          {bill.notes && (
            <div className="text-xs text-[#8A918D] mt-1.5 italic line-clamp-2">{bill.notes}</div>
          )}
        </div>
        <div className="text-right shrink-0">
          <div className="font-heading text-lg">
            <EditableAmount bill={bill} onSave={onAmount} accent={amountAccent} testId={`mobile-amount-${bill.id}`} />
          </div>
          <div className="text-[10px] text-[#8A918D] uppercase tracking-widest mt-0.5">{amountLabel}</div>
        </div>
      </div>
      <div className="mt-3 flex items-center justify-between gap-2 flex-wrap">
        <StatusDropdown bill={bill} onChange={onStatus} />
        <div className="flex items-center gap-1 flex-wrap justify-end">
          <button className="btn-ghost !px-2 !py-1 text-xs" onClick={() => onEdit(bill)} data-testid={`mobile-bill-edit-${bill.id}`}>Edit</button>
          <button
            className="btn-ghost !px-2 !py-1"
            onClick={() => onReschedule(bill)}
            aria-label="Reschedule this instance"
            title="Reschedule this instance (doesn't change the recurring day)"
            data-testid={`mobile-bill-reschedule-${bill.id}`}
          >
            <CalendarClock size={14} />
          </button>
          {!isCarryover && onDup && (
            <button className="btn-ghost !px-2 !py-1" onClick={() => onDup(bill)} aria-label="Duplicate" data-testid={`mobile-bill-dup-${bill.id}`}>
              <Copy size={14} />
            </button>
          )}
          <button className="btn-ghost !px-2 !py-1" onClick={() => onDelete(bill)} aria-label="Delete" data-testid={`mobile-bill-del-${bill.id}`}>
            <Trash2 size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}
